import { Link } from "react-router-dom";
import SEOHead from "@/components/SEOHead";
import SEOPageLayout from "@/components/SEOPageLayout";
import { SAMOAN_KEYWORDS, joinKw } from "@/config/keywords";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_custom-team-jerseys-1/artifacts/umipod3h_Samoan%20church%20choir%20uniform%20polo%20shirt.png";

export default function SamoanChurchUniforms() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Samoan Church Uniforms",
    serviceType: "Custom Samoan church polo uniform design and manufacturing",
    provider: {
      "@type": "LocalBusiness",
      name: "Tribal Designz",
      address: { "@type": "PostalAddress", addressLocality: "Auckland", addressCountry: "NZ" },
      telephone: "+64 22 101 2443",
    },
    areaServed: [{ "@type": "Country", name: "New Zealand" }],
  };

  const imageObjects = [{
    url: HERO_IMG,
    alt: "Samoan Church Choir Uniforms — custom Pacific Heritage Polo Shirts in red and white, EFKS choir Auckland NZ",
    name: "Samoan Church Uniforms — Choir Polo Set",
    representative: true,
  }];

  return (
    <>
      <SEOHead
        title="Samoan Church Uniforms NZ | Custom Polo Shirts"
        description="Samoan Church Uniforms designed in Auckland — custom polo shirts for EFKS, CCCS, AOG choirs, women's fellowship, men's fellowship, ushers & youth ministry. Free mockup."
        path="/samoan-church-uniforms"
        image={HERO_IMG}
        imageAlt="Samoan Church Choir Uniforms — custom Pacific Heritage Polo Shirts NZ"
        keywords={joinKw(SAMOAN_KEYWORDS)}
        jsonLd={jsonLd}
        imageObjects={imageObjects}
      />
      <SEOPageLayout
        heroEyebrow="EFKS · CCCS · AOG · Methodist · Catholic"
        heroH1="Samoan Church Uniforms — Custom Polo Shirts for Your EFKS"
        heroSub="Authentic Samoan church polo uniforms designed in Auckland and shipped NZ-wide. For ushers, choirs, women's and men's fellowship, youth ministry and leadership teams."
        heroImage={HERO_IMG}
        heroImageAlt="Samoan Church Choir Uniforms — Pacific Heritage Polo Shirts EFKS Auckland NZ"
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Samoan Church Uniforms" }]}
      >
        <h2>Designed for Samoan churches across New Zealand</h2>
        <p>
          Samoan churches in Auckland and across New Zealand know what
          unity looks like — and what it feels like when it's missing. When
          the choir is in mismatched shirts, when the ushers can't be picked
          out of a crowd, when the women's fellowship has nothing
          coordinated for autasi or annual flag day, the visual unity that
          should reflect the spiritual unity gets lost. <strong>Samoan
          church uniforms</strong> from Tribal Designz fix that — and
          they do it in a way that honours your heritage, not flattens it.
        </p>
        <p>
          We design custom polo uniforms for Samoan congregations of every
          denomination: EFKS, CCCS, Methodist, Catholic, AOG, PIPC, and
          independent Samoan-language ministries. Each kit is designed
          collaboratively with your minister, fellowship leader or events
          team. Your colours, your village, your church name, your fa'aSamoa
          symbols — all built into a polo that your members will want to
          wear, not just have to.
        </p>

        <h2>Polos for every group in your church</h2>
        <ul>
          <li><strong>Ushers (faifeau lalo)</strong> — coordinated welcoming team apparel</li>
          <li><strong>Choirs (aufaipese)</strong> — Sunday choir polos for senior, women's and youth choirs</li>
          <li><strong>Women's fellowship (mafutaga tina)</strong> — flag day, autasi and conference uniforms</li>
          <li><strong>Men's fellowship (mafutaga matua)</strong> — leadership team, deacons</li>
          <li><strong>Youth ministry (autalavou)</strong> — camp, youth Sunday, white Sunday polos</li>
          <li><strong>Sunday school teachers (a'oga aso sa)</strong></li>
          <li><strong>Pastoral leadership &amp; deacons</strong></li>
        </ul>

        <h2>Cultural design done with respect</h2>
        <p>
          We're a Pacific-led studio — we know which symbols belong to which
          contexts, and how to integrate fa'aSamoa motifs into modern polo
          apparel that suits both Sunday service and conference week. Common
          design elements our Samoan church clients ask for include:
        </p>
        <ul>
          <li>Siapo (tapa) patterns as subtle background prints or panels</li>
          <li>Tatau-inspired line work on shoulders, sleeves or chest band</li>
          <li>Village or district names embroidered on the chest</li>
          <li>Church name and minister's name in Samoan + English</li>
          <li>Year, location and event-specific embroidery (for conferences, camps, autasi)</li>
          <li>Hibiscus, sea turtle (laumei), and other meaningful Pacific iconography</li>
        </ul>

        <h2>Premium polo fabric, built for NZ weather</h2>
        <p>
          We use mid-weight performance-blend polos that hold their shape
          across multiple wears, don't fade after wash cycles, and breathe
          properly for Auckland's humid summers. Sizes go up to 7XL so every
          aunty, uncle and youth in your church gets a fit that works. For
          formal conference or flag day kits, we also offer button-front
          polo styles and embroidered name patches.
        </p>

        <h2>How it works for Samoan church orders</h2>
        <ol>
          <li>
            <strong>Brief.</strong> One contact from your church sends us
            the basics — group, quantity, colours, deadline, any cultural
            elements to include.
          </li>
          <li>
            <strong>Free mockup.</strong> We send back a polo mockup
            (typically two design directions). Share it with your committee.
          </li>
          <li>
            <strong>Refine &amp; approve.</strong> We revise until it's
            exactly right. You sign off the artwork.
          </li>
          <li>
            <strong>Sizing.</strong> We send a sizing guide or fit kit so
            every member gets the right fit.
          </li>
          <li>
            <strong>Produce &amp; deliver.</strong> Standard turnaround is
            2–3 weeks from artwork sign-off. Auckland pickup available.
          </li>
        </ol>

        <h2>Lead times for upcoming Samoan church events</h2>
        <p>
          Planning for White Sunday, EFKS autasi, AOG general conference, or
          Methodist district fellowship? Get your order in 4–6 weeks before
          the event to allow time for design rounds, committee approval,
          sizing and production. For urgent jobs we can offer expedited
          turnaround — talk to us first.
        </p>

        <h2>Pricing</h2>
        <p>
          Group pricing starts from <strong>$49 per polo</strong> at 50+
          units, with custom quotes available for orders over 100. See our{" "}
          <a href="/#pricing">bulk pricing table</a> for the full breakdown
          across order sizes.
        </p>

        <h2>Related</h2>
        <p>
          Looking for <Link to="/tongan-church-uniforms">Tongan church uniforms</Link>{" "}
          or <Link to="/cook-islands-uniforms">Cook Islands designs</Link>?{" "}
          We do those too. Planning a bigger event? See{" "}
          <Link to="/church-conference-uniforms">church conference uniforms</Link>.
        </p>
      </SEOPageLayout>
    </>
  );
}
